import {
  CheckCircleOutlineRounded,
  CheckCircleRounded,
  CloseRounded,
} from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CardUser, Header, StyledBox } from './styles';
import { Subscribers } from '../../../../services/requests/assinaturas/types';
import { getSignaturesById } from '../../../../services/requests/assinaturas/utils';
import { stringAvatar } from '../../../../utils/avatarGenerator';
import { sendNotificationRequest } from '../../../../services/requests/docuSign/utils';
import useAuthStore from '../../../../store/auth';
import {
  NotificationSignersDTO,
  SignersToNotification,
} from '../../../../services/requests/docuSign/type';

interface DrawerSignaturesProps {
  setOpenDrawerViewSignatures: (type: boolean) => void;
  openDrawerViewSignatures: boolean;
  fileToViewSign: any;
}

export const DrawerSignatures = ({
  setOpenDrawerViewSignatures,
  openDrawerViewSignatures,
  fileToViewSign,
}: DrawerSignaturesProps) => {
  const user = useAuthStore(state => state.user);

  const [stepSignatures, setStepSignatures] = useState('1');
  const [loadingGetToDocument, setLoadingGetToDocument] = useState(false);
  const [unsignedUsersList, setUnsignedUsersList] = useState<Subscribers[]>();
  const [signedUsersList, setsignedUsersList] = useState<Subscribers[]>();
  const [stepDrawer, setStepDrawer] = useState<any>('assinaturas');

  const getSignaturesToDocument = useCallback(async (id: string) => {
    try {
      setLoadingGetToDocument(true);
      let response: any;

      response = await getSignaturesById(id);

      if (response?.status === 200) {
        const subs = response?.data.subscribers;
        const subsFilteredSigned = subs?.filter(
          (sub: Subscribers) => sub?.isSigned === true,
        );
        const subsFilteredUnsigned = subs?.filter(
          (sub: Subscribers) => sub?.isSigned === false,
        );

        setsignedUsersList(subsFilteredSigned);
        setUnsignedUsersList(subsFilteredUnsigned);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingGetToDocument(false);
    }
  }, []);

  const handleSendReminder = useCallback(async () => {
    try {
      setLoadingGetToDocument(true);
      const signersToNotification = unsignedUsersList?.map(s => ({
        id: s?.id,
        name: s?.name,
        email: s?.email,
      }));

      const data = {
        fileId: fileToViewSign.id,
        isDocumentInBase: true,
        userId: user?.id,
        signers: signersToNotification,
      };

      const response = await sendNotificationRequest(data);

      if (response?.status === 201) {
        toast.success('Lembrete de assinatura reenviado com sucesso');
        setStepDrawer('successMessage');
      }
    } catch (err) {
      toast.error(
        'Ocorreu um erro ao reenviar o lembrete. Por favor, tente novamente mais tarde.',
      );
      console.error('handleSendReminder => ', err);
    } finally {
      setLoadingGetToDocument(false);
    }
  }, [fileToViewSign.id, unsignedUsersList, user?.id]);

  useEffect(() => {
    if (fileToViewSign?.signatures?.length > 0 && openDrawerViewSignatures) {
      getSignaturesToDocument(fileToViewSign.signatures[0].id);
    }
  }, [fileToViewSign, openDrawerViewSignatures]);

  const handleCloseDrawerSignatures = () => {
    setOpenDrawerViewSignatures(false);
  };

  return (
    <Box height="100%">
      <>
        {stepDrawer === 'assinaturas' && (
          <Box
            width="480px"
            padding="20px"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box>
              <Header>
                <CloseRounded
                  sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
                  onClick={handleCloseDrawerSignatures}
                />
                <span className="title">Assinatura</span>
              </Header>

              {loadingGetToDocument ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  width="100%"
                  mt="50px"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {signedUsersList && signedUsersList?.length > 0 && (
                    <Box>
                      <StyledBox mt="20px">
                        <CheckCircleRounded color="success" />
                        <span className="assinados">Quem assinou</span>
                      </StyledBox>
                      <Box width="100%" mt="20px">
                        {signedUsersList?.map((signed: Subscribers) => (
                          <CardUser>
                            <Box display="flex" alignItems="center" gap="10px">
                              {signed?.avatar_url ? (
                                <Avatar sx={{ width: '40px' }}>
                                  <img
                                    src={signed?.avatar_url as string}
                                    alt="Foto de perfil"
                                    style={{ width: '100%' }}
                                  />
                                </Avatar>
                              ) : (
                                <Avatar
                                  {...stringAvatar(signed.name!)}
                                  sx={{ width: '40px' }}
                                />
                              )}
                              <span className="name">{signed.name}</span>
                            </Box>
                          </CardUser>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {unsignedUsersList && unsignedUsersList?.length > 0 && (
                    <Box mt="20px">
                      <StyledBox>
                        <CheckCircleRounded color="error" />
                        <span className="pendentes">
                          Pendente de assinatura
                        </span>
                      </StyledBox>
                      <Box width="100%" mt="20px">
                        {unsignedUsersList?.map((unSigned: Subscribers) => (
                          <CardUser>
                            <Box display="flex" alignItems="center" gap="10px">
                              {unSigned?.avatar_url ? (
                                <Avatar sx={{ width: '40px' }}>
                                  <img
                                    src={unSigned?.avatar_url as string}
                                    alt="Foto de perfil"
                                    style={{ width: '100%' }}
                                  />
                                </Avatar>
                              ) : (
                                <Avatar
                                  {...stringAvatar(unSigned.name!)}
                                  sx={{ width: '40px' }}
                                />
                              )}
                              <span className="name">{unSigned.name}</span>
                            </Box>
                          </CardUser>
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'end',
              }}
            >
              <Button
                variant="outlined"
                sx={{ marginTop: '30px' }}
                onClick={() => setStepDrawer('sendEmail')}
              >
                Reenviar solicitação
              </Button>
            </Box>
          </Box>
        )}
      </>
      <>
        {stepDrawer === 'sendEmail' && (
          <Box
            width="480px"
            padding="20px"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box>
              <Header>
                <CloseRounded
                  sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
                  onClick={handleCloseDrawerSignatures}
                />
                <span className="title">Assinatura</span>
              </Header>
              {loadingGetToDocument ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  width="100%"
                  mt="50px"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt="20px"
                  >
                    <StyledBox>
                      <span className="text">Quem receberá a mensagem</span>
                    </StyledBox>
                    <Tooltip
                      TransitionProps={{ style: { fontSize: '14px' } }}
                      title="O link de assinatura de um documento poderá ser reenviado nos seguintes intervalados de tempo: 1º em 1 min, 2º em 5 min, 3º em 30 min. E a partir disso o link poderá ser reenviado de 60 em 60 min."
                    >
                      <HelpOutlineIcon />
                    </Tooltip>
                  </Box>
                  <Box width="100%" mt="20px" mb="20px">
                    {unsignedUsersList?.map(c => (
                      <CardUser>
                        <Box display="flex" alignItems="center" gap="10px">
                          {c?.avatar_url ? (
                            <Avatar sx={{ width: '40px' }}>
                              <img
                                src={c?.avatar_url as string}
                                alt="Foto de perfil"
                                style={{ width: '100%' }}
                              />
                            </Avatar>
                          ) : (
                            <Avatar
                              {...stringAvatar(c.name!)}
                              sx={{ width: '40px' }}
                            />
                          )}
                          <span className="name">{c.name}</span>
                        </Box>
                      </CardUser>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
              <Button
                variant="contained"
                color="success"
                sx={{ marginTop: '30px', color: '#ffffff' }}
                onClick={handleSendReminder}
                disabled={loadingGetToDocument}
              >
                Enviar e-mail
              </Button>
            </Box>
          </Box>
        )}
      </>
      <>
        {stepDrawer === 'successMessage' && (
          <Box width="480px" padding="20px">
            <Header>
              <CloseRounded
                sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
                onClick={handleCloseDrawerSignatures}
              />
              <span className="title">Assinatura</span>
            </Header>
            <Box
              display="flex"
              alignItems="center"
              flexDirection="column"
              gap="10px"
              mt="50px"
            >
              <CheckCircleOutlineRounded
                color="success"
                sx={{ width: '66px', height: '66px' }}
              />
              <StyledBox display="flex" flexDirection="column">
                <span className="title">
                  Assinatura solicitada com sucesso!
                </span>
                <span className="desc">
                  Os usuários serão solicitados para assinar o documento, e
                  receberão um e-mail de notificação.
                </span>
              </StyledBox>
              <Button
                variant="outlined"
                sx={{ marginTop: '20px' }}
                onClick={handleCloseDrawerSignatures}
              >
                Ok, fechar
              </Button>
            </Box>
          </Box>
        )}
      </>
    </Box>
  );
};
